import type { Dispatch, SetStateAction } from "react";
import FilterConditionSelector from "./FilterConditionSelector";
import FilterCalendar from "./FilterCalendar";
import FilterOptions from "./FilterOptions";
import { hasFilterValue, canApplyNumericFilter } from "./filtersUtil";
import type {
  CategoricalFilterMode,
  DateApplyData,
  DateInitialValues,
  FilterConditionValue,
  FilterType,
  FilterValue,
  NumericInitialValues,
  NumericCondition,
} from "./filterTypes";
import type { Option } from "./FilterOptions";

type FilterConditionsProps = {
  filterOptions: Option[];
  currentFilters: Record<string, FilterConditionValue | undefined>;
  selectedValues: FilterValue[];
  filterKey: string;
  dataType: FilterType;
  handleApply: (values: FilterValue[]) => void;
  handleDelete: () => void;
  getInitialNumericValues: () => NumericInitialValues;
  getInitialDateValues: () => DateInitialValues;
  handleApplyNumericFilter: (
    filterKey: string,
    condition: NumericCondition,
    value: number | { min: number | null; max: number | null },
  ) => void;
  handleApplyDateFilter: (filterKey: string, filterData: DateApplyData) => void;
  setOpen: (open: boolean) => void;
  setLocalSelectedValues: Dispatch<SetStateAction<FilterValue[]>>;
  mode?: CategoricalFilterMode;
  onModeChange?: (mode: CategoricalFilterMode) => void;
  useAlternativeLayout?: boolean;
  hideFooter?: boolean;
  disabled?: boolean;
};

export default function FilterConditions({
  filterOptions,
  currentFilters,
  selectedValues,
  filterKey,
  dataType,
  handleApply,
  handleDelete,
  getInitialNumericValues,
  getInitialDateValues,
  handleApplyNumericFilter,
  handleApplyDateFilter,
  setOpen,
  setLocalSelectedValues,
  mode,
  onModeChange,
  useAlternativeLayout = false,
  hideFooter = false,
  disabled = false,
}: FilterConditionsProps) {
  const commonProps = {
    onClose: () => setOpen(false),
    onClear: () => {},
    onDelete: handleDelete,
  };

  const isCurrentFilterActive = hasFilterValue(currentFilters[filterKey]);

  switch (dataType) {
    case "numeric": {
      const { condition, value, min, max } = getInitialNumericValues();
      return (
        <FilterConditionSelector
          condition={condition}
          value={value}
          minValue={min}
          maxValue={max}
          onConditionChange={() => {}}
          onValueChange={() => {}}
          onApply={(finalCondition, finalValue) => {
            const castValue = finalValue as
              | number
              | { min: number; max: number }
              | null
              | undefined;
            if (canApplyNumericFilter(finalCondition, castValue)) {
              handleApplyNumericFilter(
                filterKey,
                finalCondition as NumericCondition,
                castValue as number | { min: number; max: number },
              );
            }
          }}
          initialCondition={condition}
          initialValue={value}
          isActiveFilter={isCurrentFilterActive}
          isDropdown={true}
          hideFooter={hideFooter}
          useAlternativeLayout={useAlternativeLayout}
          disabled={disabled}
          {...commonProps}
        />
      );
    }

    case "datetime": {
      const { condition, date, range, mode, relativePreset } =
        getInitialDateValues();
      return (
        <FilterCalendar
          onApply={(filterData) => handleApplyDateFilter(filterKey, filterData)}
          initialCondition={condition}
          initialDate={date}
          initialRange={range}
          initialMode={mode}
          initialRelativePreset={relativePreset}
          isActiveFilter={isCurrentFilterActive}
          isDropdown={true}
          hideFooter={hideFooter}
          useAlternativeLayout={useAlternativeLayout}
          disabled={disabled}
          {...commonProps}
        />
      );
    }

    case "text":
    case "text_array":
    case "boolean":
    case "categorical":
    default:
      return (
        <FilterOptions
          options={filterOptions}
          selectedValues={selectedValues}
          onChange={(values) => setLocalSelectedValues(values)}
          mode={mode}
          onModeChange={
            // Exclusion (not_in) is only supported for values that serialize
            // as text filters on the backend.
            dataType === "categorical" || dataType === "text"
              ? onModeChange
              : undefined
          }
          onApply={() => {
            handleApply(selectedValues);
          }}
          onClose={() => setOpen(false)}
          onDelete={handleDelete}
          showApplyButton={!hideFooter}
          isDropdown={true}
          useAlternativeLayout={useAlternativeLayout}
          hideFooter={hideFooter}
          disabled={disabled}
        />
      );
  }
}
